import { useState, useEffect } from 'react';
import { FileText, Search, ChevronDown, ChevronUp, Award } from 'lucide-react';
import api from '../../services/api';

export default function StudentResults() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedExam, setExpandedExam] = useState(null);

  // Helper function to format numbers without unnecessary decimals
  const formatNumber = (num) => {
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return '0';
    // If decimal part is 0, show as integer, otherwise show with decimals
    return parsed % 1 === 0 ? parsed.toString() : parsed.toFixed(2);
  };

  useEffect(() => { loadResults(); }, []);

  const loadResults = async () => {
    try {
      const res = await api.get('/student/exam-results');
      if (res.data.success && res.data.data) {
        setResults(res.data.data.results || []);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const filteredResults = results.filter(r =>
    r.exam_name.toLowerCase().includes(search.toLowerCase()) ||
    r.subject_name.toLowerCase().includes(search.toLowerCase())
  );

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600 bg-green-100';
    if (percentage >= 60) return 'text-blue-600 bg-blue-100';
    if (percentage >= 40) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreGrade = (percentage) => {
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B';
    if (percentage >= 60) return 'C';
    if (percentage >= 50) return 'D';
    if (percentage >= 40) return 'E';
    return 'F';
  };

  // ordinal suffix helper (1st, 2nd, 3rd)
  const ordinalSuffix = (i) => {
    const j = i % 10, k = i % 100;
    if (j === 1 && k !== 11) return i + 'st';
    if (j === 2 && k !== 12) return i + 'nd';
    if (j === 3 && k !== 13) return i + 'rd';
    return i + 'th';
  };

  // grading used for per-exam messages: A>=75, B>=65, C>=50, W<50
  const getExamGrade = (pct) => {
    if (pct >= 75) return 'A';
    if (pct >= 65) return 'B';
    if (pct >= 50) return 'C';
    return 'W';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6 px-2 sm:px-0">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Exam Results</h1>
          <p className="text-sm sm:text-base text-gray-600">View your exam marks and component scores</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input type="text" placeholder="Search by exam or subject..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500" />
        </div>
      </div>

      {/* Results */}
      {filteredResults.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600">No exam results yet</h3>
          <p className="text-gray-400 mt-2">Your exam results will appear here once they are published</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredResults.map((result) => {
            const totalPossible = parseFloat(result.max_marks || result.exam_total_marks || 100);
            const obtainedMarks = parseFloat(result.total_marks || 0);
            const percentage = totalPossible > 0 ? Math.round((obtainedMarks / totalPossible) * 100) : 0;
            const isExpanded = expandedExam === result.exam_id;

            return (
              <div key={result.exam_id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Header */}
                <div
                  className="p-4 sm:p-6 cursor-pointer hover:bg-gray-50 transition"
                  onClick={() => setExpandedExam(isExpanded ? null : result.exam_id)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="p-2 bg-indigo-100 rounded-lg flex-shrink-0">
                        <FileText className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-800 line-clamp-1">{result.exam_name}</h3>
                        <p className="text-xs sm:text-sm text-gray-500 line-clamp-1">{result.subject_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                      <div className="text-right flex-1 sm:flex-initial">
                        <div className="flex items-center gap-2">
                          <span className="text-sm sm:text-base font-bold text-gray-800">{formatNumber(obtainedMarks)}/{formatNumber(totalPossible)}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs sm:text-sm font-medium ${getScoreColor(percentage)}`}>
                            {percentage}%
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                          <span>{new Date(result.exam_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                          {result.highest_percentage !== null && result.highest_percentage !== undefined && (
                            <span className="text-xs text-gray-500">| Class high: <span className="font-semibold text-gray-800 ml-1">{result.highest_percentage}%</span></span>
                          )}
                        </p>
                      </div>
                      <div className={`p-1.5 sm:p-2 rounded-full ${getScoreColor(percentage)}`}>
                        <Award className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      {isExpanded ? <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" /> : <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />}
                    </div>
                  </div>
                </div>

                {/* Small per-exam preview (position, class avg, grade comparison) */}
                {isExpanded && (
                  <div className="px-3 sm:px-5 pb-3 sm:pb-5 border-t border-gray-100">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-stretch">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs sm:text-sm text-gray-600">Position</p>
                        <p className="text-base sm:text-lg font-bold text-gray-800 mt-1">{result.student_position ? ordinalSuffix(result.student_position) : 'N/A'}{result.total_students ? <span className="text-sm font-normal">{' '}of {result.total_students}</span> : null}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs sm:text-sm text-gray-600">Class Average</p>
                        <p className="text-base sm:text-lg font-bold text-gray-800 mt-1">{result.average_percentage !== null && result.average_percentage !== undefined ? `${result.average_percentage}%` : 'N/A'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs sm:text-sm text-gray-600">Performance</p>
                        {(() => {
                          const totalPossible = parseFloat(result.max_marks || result.exam_total_marks || 100);
                          const obtained = parseFloat(result.total_marks || 0);
                          const studentPct = totalPossible > 0 ? Math.round((obtained / totalPossible) * 100) : 0;
                          const avgPct = result.average_percentage !== null && result.average_percentage !== undefined ? result.average_percentage : null;
                          const grade = getExamGrade(studentPct);
                          const compare = avgPct === null ? 'No class average' : (studentPct >= avgPct ? `▲ Above avg by ${studentPct - avgPct}%` : `▼ Below avg by ${avgPct - studentPct}%`);
                          return (
                            <>
                              <p className="text-base sm:text-lg font-bold text-gray-800 mt-1">{grade} <span className="text-sm font-normal">({studentPct}%)</span></p>
                              <p className="text-xs sm:text-sm text-gray-500 mt-1">{compare}</p>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Components Detail */}
                {isExpanded && result.components && result.components.length > 0 && (
                  <div className="px-3 sm:px-5 pb-3 sm:pb-5 border-t border-gray-100">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-600 mt-3 sm:mt-4 mb-2 sm:mb-3">Marks Breakdown</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                      {result.components.map((component, idx) => {
                        const obtainedComp = parseFloat(component.marks_obtained || component.obtained_marks || 0);
                        const maxComp = parseFloat(component.max_marks || 100);
                        const compPercentage = maxComp > 0 ? Math.round((obtainedComp / maxComp) * 100) : 0;
                        return (
                          <div key={idx} className="bg-gray-50 rounded-lg p-2 sm:p-3">
                            <div className="flex items-center justify-between mb-1 sm:mb-2">
                              <span className="text-xs sm:text-sm font-medium text-gray-700 truncate pr-2">{component.component_name}</span>
                              <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full flex-shrink-0 ${getScoreColor(compPercentage)}`}>
                                {getScoreGrade(compPercentage)}
                              </span>
                            </div>
                            <div className="flex items-end justify-between">
                              <span className="text-base sm:text-lg font-bold text-gray-800">
                                {formatNumber(obtainedComp)}<span className="text-xs sm:text-sm font-normal text-gray-500">/{formatNumber(maxComp)}</span>
                              </span>
                              <span className="text-xs sm:text-sm text-gray-500">{compPercentage}%</span>
                            </div>
                            <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${compPercentage >= 60 ? 'bg-green-500' : compPercentage >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{ width: `${compPercentage}%` }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {isExpanded && (!result.components || result.components.length === 0) && (
                  <div className="px-3 sm:px-5 pb-3 sm:pb-5 border-t border-gray-100">
                    <p className="text-xs sm:text-sm text-gray-400 mt-3 sm:mt-4">No component details available</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Summary: overall or per-exam when expanded */}
      {filteredResults.length > 0 && (
        expandedExam ? (() => {
          const ex = filteredResults.find(r => r.exam_id === expandedExam);
          if (!ex) return null;
          const totalPossible = parseFloat(ex.max_marks || ex.exam_total_marks || 100);
          const obtained = parseFloat(ex.total_marks || 0);
          const studentPct = totalPossible > 0 ? Math.round((obtained / totalPossible) * 100) : 0;
          const avgPct = ex.average_percentage !== null && ex.average_percentage !== undefined ? ex.average_percentage : null;
          const grade = getExamGrade(studentPct);

          return (
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-4 sm:p-6 text-white">
              <h3 className="text-lg font-semibold mb-4">Exam Summary — {ex.exam_name}</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 sm:gap-4">
                <div className="bg-white/10 rounded-lg p-3 sm:p-4">
                  <p className="text-indigo-100 text-xs sm:text-sm truncate">Your Marks</p>
                  <p className="text-xl sm:text-2xl font-bold mt-1">{formatNumber(obtained)}/{formatNumber(totalPossible)} <span className="text-sm">({studentPct}%)</span></p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 sm:p-4">
                  <p className="text-indigo-100 text-xs sm:text-sm truncate">Class High</p>
                  <p className="text-xl sm:text-2xl font-bold mt-1">{ex.highest_percentage || 'N/A'}%</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 sm:p-4">
                  <p className="text-indigo-100 text-xs sm:text-sm truncate">Position</p>
                  <p className="text-xl sm:text-2xl font-bold mt-1">{ex.student_position ? ordinalSuffix(ex.student_position) : 'N/A'}{ex.total_students ? <span className="text-sm font-normal">{' '}of {ex.total_students}</span> : ''}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 sm:p-4">
                  <p className="text-indigo-100 text-xs sm:text-sm truncate">Class Average</p>
                  <p className="text-xl sm:text-2xl font-bold mt-1">{avgPct !== null ? `${avgPct}%` : 'N/A'}</p>
                  <p className="text-xs sm:text-sm mt-1">{avgPct === null ? 'No class average' : (studentPct >= avgPct ? `▲ Above avg by ${studentPct - avgPct}%` : `▼ Below avg by ${avgPct - studentPct}%`)}</p>
                  <p className="text-xs sm:text-sm mt-1">Grade: <span className="font-semibold">{grade}</span></p>
                </div>
              </div>
            </div>
          );
        })() : (
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-4 sm:p-6 text-white">
            <h3 className="text-lg font-semibold mb-4">Results Summary</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div className="bg-white/10 rounded-lg p-3 sm:p-4">
                <p className="text-indigo-100 text-xs sm:text-sm truncate">Total Exams</p>
                <p className="text-xl sm:text-2xl font-bold mt-1">{filteredResults.length}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 sm:p-4">
                <p className="text-indigo-100 text-xs sm:text-sm truncate">Average Score</p>
                <p className="text-xl sm:text-2xl font-bold mt-1 truncate">
                  {Math.round(filteredResults.reduce((acc, r) => {
                    const total = parseFloat(r.exam_total_marks || r.max_marks || 100);
                    const obtained = parseFloat(r.total_marks || 0);
                    return acc + (total > 0 ? (obtained / total) * 100 : 0);
                  }, 0) / filteredResults.length)}%
                </p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 sm:p-4">
                <p className="text-indigo-100 text-xs sm:text-sm truncate">Highest Score</p>
                <p className="text-xl sm:text-2xl font-bold mt-1 truncate">
                  {(() => {
                    const scores = filteredResults.map(r => {
                      const total = parseFloat(r.exam_total_marks || r.max_marks || 100);
                      const obtained = parseFloat(r.total_marks || 0);
                      const studentPct = total > 0 ? Math.round((obtained / total) * 100) : 0;
                      const classPct = (r.highest_percentage !== null && r.highest_percentage !== undefined) ? parseInt(r.highest_percentage, 10) : studentPct;
                      return classPct;
                    });
                    return Math.max(...scores);
                  })()}%
                </p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 sm:p-4">
                <p className="text-indigo-100 text-xs sm:text-sm truncate">Total Marks</p>
                <p className="text-xl sm:text-2xl font-bold mt-1 truncate">
                  {formatNumber(filteredResults.reduce((acc, r) => acc + parseFloat(r.total_marks || 0), 0))}<span className="text-sm font-normal">/{formatNumber(filteredResults.reduce((acc, r) => acc + parseFloat(r.exam_total_marks || r.max_marks || 0), 0))}</span>
                </p>
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
}
